import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminRecoverPassword } from '../config/api';
import RecoveryCodeModal from './RecoveryCodeModal';

const inputStyle = {
  width: '100%',
  border: '1px solid var(--color-border)',
  background: 'rgba(255, 255, 255, 0.05)',
  padding: '12px 14px',
  fontSize: 15,
  color: '#fff',
  outline: 'none',
  fontFamily: 'var(--font-body)',
  borderRadius: 4,
  transition: 'all 0.2s ease',
};

const labelStyle = {
  display: 'block',
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#999',
  marginBottom: 8,
  fontFamily: 'var(--font-body)',
  fontWeight: 500,
};

export default function ForgotPassword() {
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newRecoveryCode, setNewRecoveryCode] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!recoveryCode || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await adminRecoverPassword(recoveryCode.trim(), newPassword);
      setNewRecoveryCode(res.data.data.recovery_code);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
      } else if (err.response?.status === 401) {
        setError('Invalid recovery code');
      } else {
        setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (newRecoveryCode) {
    return (
      <RecoveryCodeModal
        recoveryCode={newRecoveryCode}
        onConfirm={() => navigate('/admin/login')}
      />
    );
  }

  return (
    <>
      <style>{`input::placeholder { color: #666; opacity: 1; }`}</style>
      <div style={{
        minHeight: '100vh',
        background: 'var(--color-dark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{
          background: 'var(--color-surface)',
          padding: '40px 36px',
          width: '100%',
          maxWidth: 360,
          borderRadius: 4,
        }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 28,
            fontWeight: 400,
            textAlign: 'center',
            color: 'var(--color-text)',
            marginBottom: 6,
          }}>
            Account Recovery
          </h1>

          <p style={{
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--color-muted)',
            fontFamily: 'var(--font-body)',
            marginBottom: 28,
          }}>
            Enter your recovery code to set a new password
          </p>

          {error && (
            <div style={{
              background: '#fff5f5',
              borderLeft: '3px solid var(--color-error)',
              padding: '10px 12px',
              fontSize: 12,
              color: 'var(--color-error)',
              marginBottom: 18,
              fontFamily: 'var(--font-body)',
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <div style={{ marginBottom: 22 }}>
              <label style={labelStyle}>Recovery Code</label>
              <input
                type="text"
                value={recoveryCode}
                onChange={(e) => { setRecoveryCode(e.target.value); setError(''); }}
                style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '0.05em' }}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                spellCheck="false"
                onFocus={(e) => (e.target.style.borderColor = 'var(--color-accent)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
              />
            </div>

            <div style={{ marginBottom: 22 }}>
              <label style={labelStyle}>New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  style={{ ...inputStyle, paddingRight: '60px' }}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  onFocus={(e) => (e.target.style.borderColor = 'var(--color-accent)')}
                  onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
                />
                <button type="button" onClick={() => setShowNew((v) => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', fontSize: 11, color: '#999', cursor: 'pointer',
                    fontFamily: 'var(--font-body)', padding: '4px 8px', borderRadius: 3 }}>
                  {showNew ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>Confirm New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  style={{ ...inputStyle, paddingRight: '60px' }}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  onFocus={(e) => (e.target.style.borderColor = 'var(--color-accent)')}
                  onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
                />
                <button type="button" onClick={() => setShowConfirm((v) => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', fontSize: 11, color: '#999', cursor: 'pointer',
                    fontFamily: 'var(--font-body)', padding: '4px 8px', borderRadius: 3 }}>
                  {showConfirm ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: loading ? '#777' : 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                fontSize: 13,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                borderRadius: 4,
                fontWeight: 600,
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/admin/login" style={{ fontSize: 12, color: 'var(--color-muted)',
              fontFamily: 'var(--font-body)', textDecoration: 'none' }}>
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
